import React, { useState, useEffect, useRef } from 'react';
import { FileText, Download } from 'lucide-react';
import type { ConversationAttachment } from '../../types/conversation.types';
import storageService from '../../services/storage.service';
import { formatBytes } from '../CardSchemaRenderer/fileFormatUtils';

function isImageType(contentType: string): boolean {
  return contentType.startsWith('image/');
}

const AttachmentImage: React.FC<{ attachment: ConversationAttachment }> = ({ attachment }) => {
  const isBlobUrl = attachment.key.startsWith('blob:');
  const [url, setUrl] = useState<string | null>(isBlobUrl ? attachment.key : null);
  const [error, setError] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    if (isBlobUrl) return;
    mountedRef.current = true;
    storageService.getFileUrl(attachment.key).then(
      (resolved) => { if (mountedRef.current) setUrl(resolved); },
      () => { if (mountedRef.current) setError(true); },
    );
    return () => { mountedRef.current = false; };
  }, [attachment.key, isBlobUrl]);

  if (error) {
    return (
      <div className="p-2 text-xs text-gray-400">
        Не удалось загрузить изображение
      </div>
    );
  }

  if (!url) {
    return (
      <div className="w-full rounded-md bg-gray-100 flex items-center justify-center" style={{ maxWidth: 240, height: 120 }}>
        <span className="spinner size-16" />
      </div>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <img
        src={url}
        alt={attachment.filename}
        className="rounded-md cursor-pointer d-block" style={{ maxWidth: 240, maxHeight: 180 }}
      />
    </a>
  );
};

const AttachmentFile: React.FC<{ attachment: ConversationAttachment }> = ({ attachment }) => {
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    setLoading(true);
    try {
      const url = await storageService.getFileUrl(attachment.key);
      window.open(url, '_blank');
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const sizeLabel = formatBytes(attachment.size);

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={loading}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-3)',
        background: 'var(--gray-50)',
        border: '1px solid var(--gray-200)',
        borderRadius: 'var(--radius-md)',
        cursor: loading ? 'wait' : 'pointer',
        fontSize: 'var(--text-xs)',
        color: 'var(--gray-700)',
        maxWidth: '100%',
      }}
    >
      <FileText size={14} className="flex-shrink-0" />
      <span className="truncate">
        {attachment.filename}
      </span>
      <span className="text-gray-400-noshrink">{sizeLabel}</span>
      <Download size={12} className="flex-shrink-0 text-primary-500" />
    </button>
  );
};

const MessageAttachments: React.FC<{ attachments: ConversationAttachment[] }> = ({ attachments }) => {
  if (attachments.length === 0) return null;

  return (
    <div className="flex flex-col-dir gap-2 mt-2">
      {attachments.map((att) => (
        isImageType(att.content_type)
          ? <AttachmentImage key={att.key} attachment={att} />
          : <AttachmentFile key={att.key} attachment={att} />
      ))}
    </div>
  );
};

export default MessageAttachments;
